import { z } from 'zod'
import { topupStatusSchema } from './billing.ts'
import {
  businessDateSchema,
  isoDateTimeSchema,
  paginatedSchema,
  paginationQuerySchema,
} from './common.ts'

export const settingGroupSchema = z.enum([
  'pricing',
  'membership',
  'litellm',
  'doit',
  'smtp',
  'telegram',
  'google',
  'operations',
  'app',
  'app_notification',
])

export type SettingGroupName = z.infer<typeof settingGroupSchema>

/**
 * Groups the general settings page does not render.
 *
 * They belong to the desktop-app console instead, which shows them beside the
 * sidebar menu — the three things are edited together and mean little apart.
 */
export const DESKTOP_APP_SETTING_GROUPS: SettingGroupName[] = ['app', 'app_notification']

export const settingTypeSchema = z.enum([
  'string',
  'number',
  'boolean',
  'url',
  'email',
  'timezone',
])

/** Where the value currently in effect came from. */
export const settingSourceSchema = z.enum(['database', 'environment', 'default'])

export const adminSettingSchema = z.object({
  key: z.string(),
  group: settingGroupSchema,
  label: z.string(),
  description: z.string(),
  type: settingTypeSchema,
  secret: z.boolean(),
  required: z.boolean(),
  /** Always empty for secrets — a stored credential is never sent back. */
  value: z.string(),
  isSet: z.boolean(),
  source: settingSourceSchema,
  updatedAt: z.string().nullable(),
  updatedBy: z.string().nullable(),
})

export type AdminSetting = z.infer<typeof adminSettingSchema>

export const adminSettingGroupSchema = z.object({
  group: settingGroupSchema,
  label: z.string(),
  settings: z.array(adminSettingSchema),
})

export const adminSettingsSchema = z.object({ data: z.array(adminSettingGroupSchema) })

export type AdminSettings = z.infer<typeof adminSettingsSchema>

export const updateSettingsRequestSchema = z.object({
  updates: z
    .array(z.object({ key: z.string().min(1), value: z.string() }))
    .min(1)
    .max(50),
})

export type UpdateSettingsRequest = z.infer<typeof updateSettingsRequestSchema>

export const testConnectionTargetSchema = z.enum(['litellm', 'doit', 'smtp'])

export type TestConnectionTarget = z.infer<typeof testConnectionTargetSchema>

export const testConnectionRequestSchema = z.object({
  /** Where to send the probe message. Required for the SMTP test only. */
  recipient: z.email().optional(),
})

export const testConnectionResultSchema = z.object({
  ok: z.boolean(),
  message: z.string(),
})

export type TestConnectionResult = z.infer<typeof testConnectionResultSchema>

export const userKindSchema = z.enum(['web', 'device'])

export type UserKind = z.infer<typeof userKindSchema>

/** What is known about a desktop installation. Null for a web account. */
export const adminDeviceInfoSchema = z.object({
  platform: z.string().nullable(),
  appVersion: z.string().nullable(),
  lastSeenAt: z.string().nullable(),
})

export type AdminDeviceInfo = z.infer<typeof adminDeviceInfoSchema>

export const adminUserSchema = z.object({
  id: z.string(),
  email: z.email(),
  name: z.string(),
  avatarUrl: z.string().nullable(),
  kind: userKindSchema,
  device: adminDeviceInfoSchema.nullable(),
  isSuperAdmin: z.boolean(),
  isProvisioned: z.boolean(),
  bannedAt: z.string().nullable(),
  bannedReason: z.string().nullable(),
  lifetimeTopupUsd: z.number(),
  createdAt: z.string(),
})

export type AdminUser = z.infer<typeof adminUserSchema>

/** Newest first is the default: the list is most often read to see who just arrived. */
export const adminUserSortSchema = z.enum(['joinedDesc', 'joinedAsc']).default('joinedDesc')

export type AdminUserSort = z.infer<typeof adminUserSortSchema>

export const adminUserQuerySchema = paginationQuerySchema.extend({
  search: z.string().trim().max(200).optional(),
  sort: adminUserSortSchema,
  kind: userKindSchema.optional(),
})

export type AdminUserQuery = z.infer<typeof adminUserQuerySchema>

export const adminUserPageSchema = paginatedSchema(adminUserSchema)

export type AdminUserPage = z.infer<typeof adminUserPageSchema>

export const blockUserRequestSchema = z.object({
  reason: z.string().trim().max(500).default(''),
  notify: z.boolean().default(false),
})

export type BlockUserRequest = z.infer<typeof blockUserRequestSchema>

/**
 * A manual correction to a user's credit. Negative values claw credit back —
 * the ledger keeps the audit trail either way.
 */
export const adjustBalanceRequestSchema = z.object({
  usdAmount: z.number().refine((value) => value !== 0, 'Amount must not be zero'),
  note: z.string().trim().min(1).max(500),
})

export type AdjustBalanceRequest = z.infer<typeof adjustBalanceRequestSchema>

/**
 * Money actually received inside one window, counted on the moment of payment.
 *
 * `from` is sent back so the dashboard can state the period it is showing
 * rather than recomputing the boundary in the browser's own timezone, which is
 * how V1's "today" ended up seven hours off.
 */
export const topupWindowSchema = z.object({
  from: isoDateTimeSchema,
  paidCount: z.number().int(),
  totalIdr: z.number().int(),
  totalUsd: z.number(),
})

export type TopupWindow = z.infer<typeof topupWindowSchema>

export const topupReportSchema = z.object({
  timezone: z.string(),
  today: topupWindowSchema,
  week: topupWindowSchema,
  month: topupWindowSchema,
})

export type TopupReport = z.infer<typeof topupReportSchema>

/**
 * One invoice as the admin list shows it. Deliberately not the user-facing
 * `Topup`: the QRIS payload is dead weight in a list, and what an operator
 * needs instead is who paid, what the gateway calls it, and whether the credit
 * actually landed.
 */
export const adminTopupSchema = z.object({
  id: z.string(),
  status: topupStatusSchema,
  usdAmount: z.number(),
  totalIdr: z.number().int(),
  /** What the gateway says was paid. Differs from `totalIdr` only when something is wrong. */
  paidAmountIdr: z.number().int().nullable(),
  providerReference: z.string().nullable(),
  userId: z.string(),
  userName: z.string(),
  userEmail: z.string(),
  createdAt: z.string(),
  paidAt: z.string().nullable(),
  /** Null on a paid invoice means the money arrived but the credit did not. */
  creditedAt: z.string().nullable(),
})

export type AdminTopup = z.infer<typeof adminTopupSchema>

export const adminTopupQuerySchema = paginationQuerySchema.extend({
  /** Matches the invoice id, the gateway reference, or the buyer's name or email. */
  search: z.string().trim().max(200).optional(),
  status: topupStatusSchema.optional(),
  /** Jakarta calendar dates, inclusive, applied to when the invoice was created. */
  from: businessDateSchema.optional(),
  to: businessDateSchema.optional(),
})

export type AdminTopupQuery = z.infer<typeof adminTopupQuerySchema>

export const adminTopupPageSchema = paginatedSchema(adminTopupSchema)

export type AdminTopupPage = z.infer<typeof adminTopupPageSchema>

/** What a forced catalogue refresh found, so the button can say something useful. */
export const refreshModelsResultSchema = z.object({ count: z.number().int() })

export type RefreshModelsResult = z.infer<typeof refreshModelsResultSchema>

/** A sidebar entry as the admin console edits it, including disabled ones. */
export const adminMenuItemSchema = z.object({
  id: z.string(),
  label: z.string(),
  icon: z.string(),
  url: z.string(),
  position: z.number().int(),
  enabled: z.boolean(),
  createdAt: z.string(),
})

export type AdminMenuItem = z.infer<typeof adminMenuItemSchema>

export const adminMenuSchema = z.object({ data: z.array(adminMenuItemSchema) })

export type AdminMenu = z.infer<typeof adminMenuSchema>

export const menuItemRequestSchema = z.object({
  label: z.string().trim().min(1).max(64),
  /** A lucide icon name. Not checked here — the app decides what it can draw. */
  icon: z.string().trim().min(1).max(64),
  url: z.url(),
  position: z.number().int().min(0).max(999).default(0),
  enabled: z.boolean().default(true),
})

export type MenuItemRequest = z.infer<typeof menuItemRequestSchema>

export const updateMenuItemRequestSchema = menuItemRequestSchema.partial().refine(
  (value) => Object.keys(value).length > 0,
  { message: 'Provide at least one field to update' },
)

export type UpdateMenuItemRequest = z.infer<typeof updateMenuItemRequestSchema>
